import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { genAiGoalsReportInputMapper } from '@cpd-app/core-module/mappers/gen-ai-goals-report-input.mapper';
import { AiGoalsReportInput, GoalsInput } from '@cpd-app/generated';
import { programGoalChange } from './program-goal-change';
import { MessageComponent } from '@cpd-app/shared-module/components/message/message.component';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  retry,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { GoalActions } from '.';
import {
  certificationGoalChange,
  credentialGoalChange
} from './credential-row';
import { viaGoalChange } from './via-goal-change';
import { viaReportFallbackPoll } from './via-status-fallback';
import {
  EDUCATION_GOALS_CLEARED,
  careerGoalCutsEducation
} from './career-change';
import { GoalDataService } from '../../core-module/services';
import {
  CPD_ANALYTICS_EVENT,
  GoogleAnalyticsService
} from '../../core-module/services/google-analytics/google-analytics-service.service';
import { OccupationDetailFacade } from '../occupation-detail';
import { PathwayFacade } from '../pathway';
import { UserProfileActions, UserProfileFacade } from '../user-profile';
import { GoalFacade } from './goal.facade';
import { VIA_GEN_STATUS } from '@cpd-app/shared-module/constants';
import { TypedAction } from '@ngrx/store/src/models';

@Injectable()
export class GoalEffects {
  private notificationRef: MatSnackBarRef<MessageComponent>;

  public loadGoals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.loadGoals, UserProfileActions.saveUserProfileToState),
      debounceTime(500),
      switchMap(() =>
        this.goalDataService.getUserGoals().pipe(
          /*
           * GR.6. A read still on its way when a goal write SUCCEEDS is
           * dropped. It may have read the row before the write, and answering
           * after the write's own answer it put the old goals back: a career
           * goal the survey had just saved read as missing, and the header
           * relocked. The write answers with the goals it saved, so nothing is
           * lost. Cut on success only: a write that fails changed nothing and
           * resets the goal state, so the read is then the way back to knowing
           * the goals. A read that starts after the write is delivered.
           */
          takeUntil(
            this.actions$.pipe(ofType(GoalActions.goalsUpdatedSuccess))
          ),
          map((userGoals) => GoalActions.goalsLoadedSuccess({ userGoals })),
          catchError((error) => {
            return of(GoalActions.goalsLoadedFailure({ error }));
          })
        )
      )
    );
  });

  public refreshViaInsight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoalActions.refreshViaInsight),
      withLatestFrom(
        this.userProfileFacade.userProfile$,
        this.goalFacade.goals$,
        this.occupationDetailFacade.occupationForExportPdf$
      ),
      switchMap(([, userProfile, goals, occupation]) => {
        const surveyData: AiGoalsReportInput = genAiGoalsReportInputMapper(
          userProfile,
          goals,
          occupation
        );
        return this.goalDataService.refreshViaInsights(surveyData).pipe(
          map((aiGoalsReport) =>
            GoalActions.genAiGoalsReportSuccess({ aiGoalsReport })
          ),
          catchError((error) => {
            return of(GoalActions.genAiGoalsReportFailure({ error }));
          })
        );
      })
    )
  );

  public connectGenStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoalActions.connectGenStatus),
      switchMap(() =>
        this.goalDataService.getGenViaInsightStatus().pipe(
          filter((status) => !!status),
          distinctUntilChanged(),
          switchMap((status) => {
            /*
             * V-2. `failed` is TERMINAL and must disconnect, exactly as
             * `completed` does.
             *
             * Only `completed` did. A failed frame therefore dispatched
             * genAiGoalsReport() -- ANOTHER generation attempt -- and left
             * statusConnect true forever, so the container never re-armed and
             * the stream was never torn down. The one status that means "stop"
             * was the one that kept it running.
             *
             * It also must NOT re-request the report: the backend has marked
             * it terminal and will not regenerate it, so the extra dispatch
             * only re-fetches the same dead row. Retrying is an explicit
             * learner action now, on the panel.
             */
            const isTerminal =
              status === VIA_GEN_STATUS.completed ||
              status === VIA_GEN_STATUS.failed;

            /*
             * A failed frame now WRITES the failure into the store.
             *
             * It used to dispatch nothing at all, which left the panel reading
             * the stale `in-progress` from the page's own initial load -- so
             * the skeleton ran forever over a report that had already failed,
             * and the failed panel below it, retry button and all, was
             * unreachable. Disconnecting without recording why is not handling
             * the status, it is discarding it.
             *
             * Still not genAiGoalsReport(): that is a MUTATION and would kick
             * off another generation, which is the loop V-2 removed.
             */
            const actions: TypedAction<string>[] =
              status === VIA_GEN_STATUS.failed
                ? [GoalActions.genStatusReportFailed()]
                : [GoalActions.genAiGoalsReport()];

            if (isTerminal) {
              actions.push(GoalActions.disConnectGenStatus());
            }

            return actions;
          }),
          /**
           * GR-4. A DROPPED STREAM RECONNECTS INSTEAD OF STRANDING THE MEMBER.
           *
           * Whatever kills this observable -- a parse fault, a proxy closing
           * an idle connection, a network blip -- used to end the story. The
           * catchError below sets statusConnect false and nothing re-arms,
           * because the only thing that re-arms is a new emission of
           * aiGoalsReport$, which is fed by this very stream. So one error
           * anywhere left the spinner running while the backend quietly
           * finished the report.
           *
           * Bounded on purpose. Three attempts covers a transient drop; an
           * unbounded retry against a genuinely broken endpoint is a hot loop
           * that the member cannot see and cannot stop. After three, the
           * failure surfaces as it does now and the panel's own retry is the
           * way back.
           */
          retry({ count: 3, delay: 3000 }),
          catchError((error) =>
            of(GoalActions.connectGenStatusFailure({ error }))
          )
        )
      )
    )
  );

  /**
   * AI-5. When the status stream is gone for good, read the report on a timer.
   *
   * connectGenStatus$ above ends in connectGenStatusFailure after its retries,
   * and until this nothing ever read the report again: it finished on the
   * backend while the page showed "Via is reading this section" until reload.
   * See via-status-fallback.ts for the stop and give-up rules.
   *
   * exhaustMap, not switchMap: every poll result re-arms the page's stream,
   * which can fail again, and restarting the poll on each failure would reset
   * its give-up clock and let it run forever.
   */
  public pollViaReportWhenStreamFails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoalActions.connectGenStatusFailure),
      exhaustMap(() =>
        viaReportFallbackPoll(
          this.goalFacade.aiGoalsReport$.pipe(map((report) => report?.status))
        )
      )
    )
  );

  public loadGoalsReportDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        GoalActions.loadGoalsReportDetails,
        UserProfileActions.saveUserProfileToState
      ),
      switchMap(() => this.goalFacade.isEnableToGetReturnLink$),
      filter((isEnable) => isEnable),
      debounceTime(500),
      switchMap(() =>
        this.goalDataService.getGoalsReportDetails().pipe(
          map((data) =>
            data
              ? GoalActions.loadGoalsReportDetailsSuccess({ data })
              : GoalActions.loadGoalsReportDetailsFailure({
                  error: new Error('goalsReportDetails returned no data')
                })
          ),
          catchError((error) => {
            return of(GoalActions.loadGoalsReportDetailsFailure({ error }));
          })
        )
      )
    );
  });

  public setCareerGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setCareerGoal),
      map(({ onetCode, occupationTitle, jobId }) => {
        const updatedGoals = {
          onetCode,
          occupationTitle,
          jobId: jobId ? jobId.toString() : null
        } as GoalsInput;

        return GoalActions.updateGoals({ userGoals: updatedGoals });
      })
    );
  });

  /**
   * CPD-665. A certification and a Registry credential are ONE goal.
   *
   * The change is built by `certificationGoalChange` rather than inline,
   * because its other half lives in `setCredentialGoal$` below and the two
   * halves had already drifted: one cleared the other, the other did not.
   * See `credential-row.ts`.
   */
  public setCertificationGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setCertificationGoal),
      map(({ certificationId }) =>
        GoalActions.updateGoals({
          userGoals: certificationGoalChange(certificationId)
        })
      )
    );
  });

  /**
   * CRED.1. Through `updateGoals` like every other goal, deliberately.
   *
   * That effect is the single writer: it merges the change onto the current
   * goals before sending, so a credential goal cannot blank a member's career
   * or school goal on its way past. A second write path would lose that.
   */
  public setCredentialGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setCredentialGoal),
      map(({ credentialCtid, credentialTitle }) =>
        GoalActions.updateGoals({
          userGoals: credentialGoalChange(credentialCtid, credentialTitle)
        })
      )
    );
  });

  public setLicenseGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setLicenseGoal),
      map(({ licenseId }) => {
        const updatedGoals = {
          licenseId
        } as GoalsInput;

        return GoalActions.updateGoals({ userGoals: updatedGoals });
      })
    );
  });

  public setInstitutionGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setInstitutionGoal),
      map(({ schoolId }) => {
        /*
         * CPD-661 item 9. The school and nothing else.
         *
         * Clearing the school used to clear the program with it, from when the
         * school was chosen first and the program picked from its catalogue.
         * The program is chosen first now and does not belong to a school, so
         * a member removing a school is usually about to pick another one for
         * the same program. See `career-change.ts` for the whole rule.
         */
        const updatedGoals = { schoolId } as GoalsInput;

        return GoalActions.updateGoals({ userGoals: updatedGoals });
      })
    );
  });

  public setInstitutionProgramGoal$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setInstitutionProgramGoal),
      map(({ schoolId, schoolProgramId, schoolProgramEdLevelId }) => {
        // CPD-679: a program write that names no school clears the school.
        // The rule and its history are in program-goal-change.ts.
        const updatedGoals = programGoalChange(
          schoolId,
          schoolProgramId,
          schoolProgramEdLevelId
        ) as GoalsInput;

        return GoalActions.updateGoals({ userGoals: updatedGoals });
      })
    );
  });

  /**
   * A goal set from the Via panel, MERGED ONTO THE GOALS ON FILE.
   *
   * It used to send the four fields Via knows about and nothing else, and the
   * mutation writes every column from what it is sent -- so "Set as career
   * goal" in chat silently wiped the member's school, programme,
   * certification, licence and Registry credential. `viaGoalChange` is that
   * merge, and carries the rest of the reasoning.
   */
  public setGoalsFromAiChat$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setGoalsFromAiChat),
      withLatestFrom(this.goalFacade.goals$),
      switchMap(([{ goals }, onFile]) =>
        this.goalDataService.updateUserGoals(viaGoalChange(goals, onFile))
      ),
      /*
       * DS-T16 goal_set, source 'via'. This effect and updateGoals$ below are
       * the only two writers, and they are the ui/via split: a goal reaching
       * the database through here was extracted by Via from a conversation,
       * never typed by the learner. Tagged at the success map rather than at
       * the action, so an attempt that fails the mutation is not counted as a
       * goal set.
       */
      tap(() =>
        this.googleAnalytics.trackEvent(CPD_ANALYTICS_EVENT.goalSet, {
          source: 'via'
        })
      ),
      map((goals) => GoalActions.goalsUpdatedSuccess({ userGoals: goals })),
      catchError((error) => {
        return of(GoalActions.goalsUpdatedFailure({ error }));
      })
    );
  });

  public updateGoals$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.updateGoals),
      withLatestFrom(
        this.goalFacade.goals$,
        this.occupationDetailFacade.occupationDetail$,
        this.pathwayFacade.pathwayId$
      ),
      switchMap(([input, goals, occupationOpen, pathwayId]) => {
        /*
         * GR.6. A WRITE THAT NAMES A CAREER IS FOR THAT CAREER.
         *
         * The career used to come from the open occupation page, or else from
         * the goals on file, whatever the write named. The occupation page
         * always names its own occupation, so that never showed. The Skills
         * Survey's goals step has no occupation page open: its write went out
         * with a null career, which `careerGoalCutsEducation` read as a
         * removal, clearing every education goal.
         *
         * So a named career wins, and the open page (title, job id, custom
         * code, pathway) is used only when it IS that occupation, which keeps
         * the occupation page exactly as it was. A write that names no career
         * (a school, a program, a credential) resolves it as before.
         */
        const named = input.userGoals?.onetCode;
        const occupation =
          !named || occupationOpen?.onetCode === named ? occupationOpen : null;
        const onetCode = named || occupation?.onetCode || goals?.onetCode;
        const occupationTitle = named && !occupation
          ? input.userGoals.occupationTitle
          : occupation?.title || goals?.occupationTitle;
        const jobId = named && !occupation
          ? input.userGoals.jobId ?? null
          : occupation?.jobId || goals?.jobId;
        const customOnetCode = occupation?.cpbonetcode || null;

        let goalsInput = {
          ...goals,
          ...input.userGoals,
          onetCode,
          occupationTitle,
          jobId,
          customOnetCode,
          // If user has custom onet code, we need to pass pathwayId to backend
          pathwayId: customOnetCode && pathwayId ? pathwayId : null
        } as GoalsInput;

        if (input.userGoals.onetCode === null) {
          /*
           * REMOVING THE CAREER GOAL REMOVES EVERYTHING CHOSEN FOR IT.
           *
           * CPD-661 item 8. The 22 Sep rule here nulled the career columns
           * and carried the rest through; Jackson reversed it on 23 Sep --
           * see `career-change.ts`. Named column by column rather than
           * `goalsInput = {}`, so a field added to the goals later does not
           * silently join the cascade.
           */
          goalsInput = {
            ...goalsInput,
            ...EDUCATION_GOALS_CLEARED,
            onetCode: null,
            occupationTitle: null,
            jobId: null,
            customOnetCode: null,
            pathwayId: null
          };
        } else if (
          'onetCode' in input.userGoals &&
          careerGoalCutsEducation(goals?.onetCode, onetCode)
        ) {
          /*
           * CHANGING THE CAREER GOAL IS REMOVING THE OLD ONE.
           *
           * Only for a write that names the career (`setCareerGoal$`). Every
           * other write resolves `onetCode` from the page open behind it, and
           * must not be read as the member switching career.
           */
          goalsInput = { ...goalsInput, ...EDUCATION_GOALS_CLEARED };
        }

        return this.goalDataService.updateUserGoals(goalsInput).pipe(
          // DS-T16 goal_set, source 'ui'. The learner set this themselves.
          // Includes the clear-goal case above, which arrives here with an
          // empty goalsInput -- worth knowing that clearing is as instrumented
          // as setting, because a spike in one is only readable against the
          // other.
          tap(() =>
            this.googleAnalytics.trackEvent(CPD_ANALYTICS_EVENT.goalSet, {
              source: 'ui'
            })
          ),
          switchMap((goals) => [
            GoalActions.goalsUpdatedSuccess({ userGoals: goals })
          ]),
          catchError((error) => {
            return of(GoalActions.goalsUpdatedFailure({ error }));
          })
        );
      })
    );
  });

  public updateLearningSuggestions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.setLearningSuggestionsFromAiChat),
      switchMap((input) => {
        return this.goalDataService
          .updateLearningSuggestions(input.conversationId, input.messageId)
          .pipe(
            tap(() => {
              this.notificationRef = this.snackBar.openFromComponent(
                MessageComponent,
                {
                  horizontalPosition: 'right',
                  verticalPosition: 'top',
                  duration: 5000,
                  panelClass: 'remind',
                  data: {
                    title: `You have set a Course / Certificate goal!`,
                    closeFn: () => {
                      this.notificationRef.dismiss();
                    },
                    callbackFn: () => {
                      this.notificationRef.dismiss();
                    },
                    callbackLabel: 'You can review it in your Goals Report now'
                  }
                }
              );
              return this.notificationRef;
            }),
            switchMap((aiDeepInsights) => [
              GoalActions.setLearningSuggestionsFromAiChatSuccess({
                aiDeepInsights
              })
            ]),
            catchError((error) => {
              return of(
                GoalActions.setLearningSuggestionsFromAiChatFailure({ error })
              );
            })
          );
      })
    );
  });

  public genAiGoalsReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.genAiGoalsReport),
      withLatestFrom(
        this.userProfileFacade.userProfile$,
        this.userProfileFacade.isSkillsSurveyComplete$,
        this.goalFacade.goals$,
        this.occupationDetailFacade.occupationForExportPdf$
      ),
      filter(
        ([, userProfile, isSkillSurveyComplete, goals, occupation]) =>
          !!userProfile &&
          !!occupation &&
          !!goals?.occupationTitle &&
          isSkillSurveyComplete
      ),
      switchMap(([, userProfile, , goals, occupation]) => {
        const surveyData: AiGoalsReportInput = genAiGoalsReportInputMapper(
          userProfile,
          goals,
          occupation
        );
        return this.goalDataService.genAiGoalsReport(surveyData).pipe(
          map((aiGoalsReport) =>
            GoalActions.genAiGoalsReportSuccess({ aiGoalsReport })
          ),
          catchError((error) => {
            return of(GoalActions.genAiGoalsReportFailure({ error }));
          })
        );
      })
    );
  });

  public loadAiDeepInsight$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GoalActions.loadAiDeepInsight),
      withLatestFrom(this.cognitoAuthFacade.isAuthenticated$),
      filter(([, isAuthenticated]) => !!isAuthenticated),
      switchMap(() => {
        return this.goalDataService.getAiDeepInsight().pipe(
          map((aiDeepInsights) =>
            GoalActions.loadAiDeepInsightSuccess({ aiDeepInsights })
          ),
          catchError((error) => {
            return of(GoalActions.loadAiDeepInsightFailure({ error }));
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private goalDataService: GoalDataService,
    private goalFacade: GoalFacade,
    private userProfileFacade: UserProfileFacade,
    private cognitoAuthFacade: CognitoAuthFacade,
    private occupationDetailFacade: OccupationDetailFacade,
    private snackBar: MatSnackBar,
    private pathwayFacade: PathwayFacade,
    private googleAnalytics: GoogleAnalyticsService
  ) {}
}
